import { ActionIcon, Button, Group, Paper, ScrollArea, SimpleGrid, Stack, Text } from '@mantine/core';
import { IconMessage, IconPaperclip, IconX } from '@tabler/icons-react';
import dayjs from 'dayjs';

import classes from './ExpandedRowPanel.module.css';
import type { QuickPrompt } from './QuickPromptChips.tsx';
import { QuickPromptChips } from './QuickPromptChips.tsx';

export interface ExpandedRowPanelProps {
  record: Record<string, unknown>;
  onCollapse: () => void;
  onAddAsAttachment?: () => void;
  onAskAbout?: () => void;
  quickPrompts?: QuickPrompt[];
  onQuickPromptClick?: (prompt: QuickPrompt) => void;
}

/**
 * Formats a value for display in the expanded row panel.
 * Uses type detection similar to column formatters.
 */
function formatValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '-';
  }

  // Boolean
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  // Date detection - ISO 8601 strings or Date objects
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return dayjs(value).format('MMM D, YYYY h:mm A');
  }

  if (typeof value === 'string') {
    const isoPattern = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:\d{2})?)?$/;
    if (isoPattern.test(value)) {
      const parsed = dayjs(value);
      if (parsed.isValid()) {
        const hasTime = value.includes('T');
        return hasTime ? parsed.format('MMM D, YYYY h:mm A') : parsed.format('MMM D, YYYY');
      }
    }
  }

  // Number
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value.toLocaleString();
  }

  // Object - stringify for display
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
}

/**
 * Converts accessor to a display-friendly label.
 * Handles camelCase and snake_case.
 */
function formatLabel(accessor: string): string {
  return accessor
    .replace(/([a-z])([A-Z])/g, '$1 $2') // camelCase to spaces
    .replace(/_/g, ' ') // snake_case to spaces
    .replace(/\b\w/g, (c) => c.toUpperCase()); // Capitalize first letter of each word
}

/**
 * Gets the record identifier for display in the header.
 */
function getRecordIdentifier(record: Record<string, unknown>): string {
  // Prefer externalId for Cognite records
  if (typeof record.externalId === 'string' && record.externalId) {
    return record.externalId;
  }

  // Fall back to name or title
  if (typeof record.name === 'string' && record.name) {
    return record.name;
  }

  if (typeof record.title === 'string' && record.title) {
    return record.title;
  }

  // Fall back to first string field
  const firstStringValue = Object.values(record).find((v) => typeof v === 'string' && v.length > 0);
  if (firstStringValue) {
    return firstStringValue as string;
  }

  return 'Record Details';
}

/**
 * Panel component for expanded row content.
 * Shows record details, quick actions, and suggested prompts.
 */
export function ExpandedRowPanel({
  record,
  onCollapse,
  onAddAsAttachment,
  onAskAbout,
  quickPrompts,
  onQuickPromptClick,
}: ExpandedRowPanelProps) {
  // Filter out internal 'id' field from display
  const displayFields = Object.entries(record).filter(([key]) => key !== 'id');

  const hasQuickActions = onAddAsAttachment || onAskAbout;
  const hasQuickPrompts = quickPrompts && quickPrompts.length > 0 && onQuickPromptClick;

  return (
    <Paper className={classes.panel} p="md" radius="sm">
      {/* Header */}
      <Group className={classes.header} justify="space-between" mb="md">
        <Text fw={600} size="sm">
          {getRecordIdentifier(record)}
        </Text>
        <ActionIcon aria-label="Collapse row" onClick={onCollapse} size="sm" variant="subtle">
          <IconX size={16} />
        </ActionIcon>
      </Group>

      {/* Detail section */}
      <ScrollArea.Autosize className={classes.scrollArea} mah={300}>
        <SimpleGrid cols={2} spacing="xs" verticalSpacing="xs">
          {displayFields.map(([key, value]) => (
            <div key={key}>
              <Text c="dimmed" className={classes.fieldLabel} size="xs">
                {formatLabel(key)}
              </Text>
              <Text className={classes.fieldValue} size="sm">
                {formatValue(value)}
              </Text>
            </div>
          ))}
        </SimpleGrid>
      </ScrollArea.Autosize>

      {/* Quick actions */}
      {hasQuickActions && (
        <Group className={classes.actions} gap="xs" mt="md">
          {onAddAsAttachment && (
            <Button leftSection={<IconPaperclip size={14} />} onClick={onAddAsAttachment} size="xs" variant="light">
              Add as attachment
            </Button>
          )}
          {onAskAbout && (
            <Button leftSection={<IconMessage size={14} />} onClick={onAskAbout} size="xs" variant="light">
              Ask about this
            </Button>
          )}
        </Group>
      )}

      {/* Quick prompts */}
      {hasQuickPrompts && (
        <Stack gap="xs" mt="md">
          <Text c="dimmed" size="xs">
            Suggested actions
          </Text>
          <QuickPromptChips onPromptClick={onQuickPromptClick} prompts={quickPrompts} />
        </Stack>
      )}
    </Paper>
  );
}
